import React, { useEffect, useState, useCallback } from 'react';
import {
  LayoutDashboard, GitPullRequest, Bot, CalendarClock, Workflow,
  Sparkles, Package, Settings as SettingsIcon, FolderGit2,
  CircleDot, Activity, ScrollText, ListTodo, Network, FolderTree,
  type LucideIcon,
} from 'lucide-react';
import { api } from './lib/api';
import { usePRs, usePR, useConfig, triggerReview, interactReview, resetDB, simulateWebhook } from './lib/data';
import { useTheme } from './lib/theme';
import { AppShell, type NavGroup } from './components/shell/app-shell';
import { KanbanBoard } from './components/KanbanBoard';
import { Drawer } from './components/Drawer';
import { Dashboard } from './components/Dashboard';
import { DashboardOverview } from './components/DashboardOverview';
import { AgentRunners } from './components/AgentRunners';
import { ScheduleView } from './components/ScheduleView';
import { Settings } from './components/Settings';
import { PrDetails } from './components/PrDetails';
import { SbomManager } from './components/SbomManager';
import { Capabilities } from './components/Capabilities';
import { WorkflowList } from './components/workflows/WorkflowList';
import { WorkflowEditor } from './components/workflows/WorkflowEditor';
import { AuthPortal } from './components/AuthPortal';
import { AccountModal } from './components/AccountModal';
import { OrgProvider, useOrg } from './lib/org-context';
import { ProjectsView } from './components/ProjectsView';
import type { ReviewComment } from './types';
import { ProjectDetail } from './components/ProjectDetail';
import { Issues } from './components/Issues';
import { Reliability } from './components/Reliability';
import { Rules } from './components/Rules';
import { EdgeQueue } from './components/EdgeQueue';
import { Repos } from './components/Repos';
import { AgentTasks } from './components/AgentTasks';

type View =
  | 'dashboard' | 'prs' | 'pr-detail' | 'tasks' | 'agent-tasks' | 'agents' | 'schedules' | 'sbom'
  | 'capabilities' | 'settings' | 'workflows' | 'workflow-edit'
  | 'projects' | 'project-detail'
  | 'repos' | 'issues' | 'reliability' | 'rules' | 'edge-queue';

interface SessionUser { id: string; username?: string | null; name?: string | null; email?: string | null; role: 'Admin' | 'Reviewer'; createdAt?: string | null }

type NavEntry = { id: View; label: string; icon: LucideIcon; disabled?: boolean };

const WORKSPACE_NAV: NavEntry[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'projects', label: 'Repositories', icon: FolderGit2 },
  { id: 'repos', label: 'Repo Snapshots', icon: FolderTree },
  { id: 'prs', label: 'Pull Requests', icon: GitPullRequest },
  { id: 'tasks', label: 'Tasks', icon: ListTodo },
  { id: 'agent-tasks', label: 'Agent Tasks', icon: Bot },
  { id: 'issues', label: 'Issues', icon: CircleDot },
  { id: 'reliability', label: 'Reliability', icon: Activity },
  { id: 'workflows', label: 'Workflows', icon: Workflow },
];

const MANAGEMENT_NAV: NavEntry[] = [
  { id: 'agents', label: 'Agent Runners', icon: Bot },
  { id: 'edge-queue', label: 'Edge Queue', icon: Network },
  { id: 'capabilities', label: 'Capabilities', icon: Sparkles },
  { id: 'sbom', label: 'SBOM', icon: Package },
  { id: 'rules', label: 'Rules', icon: ScrollText },
  { id: 'schedules', label: 'Schedules', icon: CalendarClock },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const PAGE_TITLE: Record<string, string> = {
  projects: 'Repositories',
  'project-detail': 'Repository',
  repos: 'Repo Snapshots',
  'agent-tasks': 'Agent Tasks',
  dashboard: 'Dashboard',
  tasks: 'Tasks',
  prs: 'Pull Requests',
  'pr-detail': 'Pull Request',
  agents: 'Agent Runners',
  schedules: 'Schedules',
  issues: 'Issues',
  reliability: 'Reliability',
  rules: 'Rules',
  'edge-queue': 'Edge Queue',
  workflows: 'Workflows',
  'workflow-edit': 'Workflow Editor',
  capabilities: 'Capabilities',
  sbom: 'SBOM',
  settings: 'Settings',
};

const NAV_GROUPS: NavGroup[] = [
  { label: 'Workspace', items: WORKSPACE_NAV },
  { label: 'Management', items: MANAGEMENT_NAV },
];

export default function App() {
  const [view, setView] = useState<View>('dashboard');
  const [selectedPr, setSelectedPr] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<{ owner: string; repo: string } | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { theme, toggle, setTheme } = useTheme();

  // Probe the session once on boot.
  useEffect(() => {
    (async () => {
      try {
        const session = await api<{ user?: SessionUser }>('/api/auth/session');
        if (session?.user) setUser(session.user);
      } catch { /* not signed in (or Worker offline) */ }
      finally { setSessionChecked(true); }
    })();
  }, []);

  const onSelectPr = useCallback((id: string) => { setSelectedPr(id); }, []);
  const onClosePr = useCallback(() => { setSelectedPr(null); }, []);
  const onSelectProject = useCallback((owner: string, repo: string) => {
    setSelectedProject({ owner, repo });
    setView('project-detail');
  }, []);
  const onBack = useCallback(() => setView('prs'), []);
  const onTriggerReview = useCallback(async (prId: string) => { await triggerReview(prId); }, []);
  const onInteractComment = useCallback(async (prId: string, commentId: string, action: 'accept' | 'refine' | 'dismiss' | 'apply', userFeedback?: string) => {
    return interactReview(prId, commentId, action, userFeedback);
  }, []);
  const onResetDb = useCallback(async () => { await resetDB(); }, []);
  const onSaveConfig = useCallback(async (patch: any) => {
    await api('/api/config', { method: 'POST', body: JSON.stringify(patch) });
  }, []);
  const onSimulateWebhook = useCallback(async (input: { owner: string; repo: string; prNumber: number; baseSha: string; headSha: string; title?: string; body?: string }) => {
    return simulateWebhook(input);
  }, []);
  const onSignOut = useCallback(async () => {
    try {
      const res = await api<{ ok?: boolean; logoutUrl?: string }>('/api/auth/signout', { method: 'POST' });
      if (res?.logoutUrl) {
        window.location.href = res.logoutUrl;
        return;
      }
    } catch { /* cookie clear is best-effort */ }
    setUser(null);
  }, []);

  if (!sessionChecked) {
    return (
      <main className="grid min-h-dvh place-items-center bg-background text-sm text-muted-foreground">
        Loading session…
      </main>
    );
  }

  if (!user) {
    return <AuthPortal />;
  }

  return (
    <OrgProvider>
      <Console
        user={user}
        view={view}
        setView={setView}
        selectedPr={selectedPr}
        onSelectPr={onSelectPr}
        onClosePr={onClosePr}
        theme={theme}
        toggle={toggle}
        onBack={onBack}
        onTriggerReview={onTriggerReview}
        onInteractComment={onInteractComment}
        onResetDb={onResetDb}
        onSaveConfig={onSaveConfig}
        onSimulateWebhook={onSimulateWebhook}
        onSignOut={onSignOut}
        onOpenAccount={() => setAccountOpen(true)}
        selectedProject={selectedProject}
        onSelectProject={onSelectProject}
      />
      {accountOpen && (
        <AccountModal
          open
          onClose={() => setAccountOpen(false)}
          user={user}
          theme={theme}
          onSetTheme={setTheme}
          onSignOut={() => { setAccountOpen(false); onSignOut(); }}
        />
      )}
    </OrgProvider>
  );
}

/** Authenticated shell. Lives inside OrgProvider so it can read the org context. */
function Console(props: {
  user: SessionUser;
  view: View;
  setView: (v: View) => void;
  selectedPr: string | null;
  onSelectPr: (id: string) => void;
  onClosePr: () => void;
  selectedProject: { owner: string; repo: string } | null;
  onSelectProject: (owner: string, repo: string) => void;
  theme: ReturnType<typeof useTheme>['theme'];
  toggle: () => void;
  onBack: () => void;
  onTriggerReview: (prId: string) => Promise<void>;
  onInteractComment: (prId: string, commentId: string, action: 'accept' | 'refine' | 'dismiss' | 'apply', userFeedback?: string) => Promise<{ message: string; comment?: ReviewComment }>;
  onResetDb: () => Promise<void>;
  onSaveConfig: (patch: any) => Promise<void>;
  onSimulateWebhook: (input: { owner: string; repo: string; prNumber: number; baseSha: string; headSha: string; title?: string; body?: string }) => Promise<{ message: string; prId: string; taskId: string }>;
  onSignOut: () => Promise<void>;
  onOpenAccount: () => void;
}) {
  const { user, view, setView, selectedPr, onSelectPr, onClosePr, selectedProject, onSelectProject, theme, toggle,
    onBack, onTriggerReview, onInteractComment, onResetDb, onSaveConfig, onSimulateWebhook, onSignOut, onOpenAccount } = props;
  const { orgs, current: currentOrg, setCurrent: onSelectOrg } = useOrg();
  const { prs, loading: loadingPrs } = usePRs();
  const { data: prDetail, loading: loadingPr } = usePR(selectedPr);
  const config = useConfig();

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const onOpenWorkflow = useCallback((id: string) => { setSelectedWorkflowId(id); setView('workflow-edit'); }, [setView]);
  const onBackToWorkflows = useCallback(() => setView('workflows'), [setView]);

  const renderView = () => {
    switch (view) {
      case 'projects':
        return <ProjectsView onSelectProject={onSelectProject} />;
      case 'project-detail':
        if (selectedProject) {
          return <ProjectDetail owner={selectedProject.owner} repo={selectedProject.repo} onBack={() => setView('projects')} />;
        }
        return <ProjectsView onSelectProject={onSelectProject} />;
      case 'repos':
        return <Repos />;
      case 'dashboard':
        return <DashboardOverview prs={prs ?? []} onSelectPr={onSelectPr} loading={loadingPrs} />;
      case 'tasks':
        return <KanbanBoard prs={prs ?? []} onSelectPr={onSelectPr} loading={loadingPrs} />;
      case 'agent-tasks':
        return <AgentTasks />;
      case 'prs':
        return <Dashboard prs={prs ?? []} onSelectPr={onSelectPr} onResetDb={onResetDb} loading={loadingPrs} />;
      case 'pr-detail':
        if (prDetail) {
          return <PrDetails prId={prDetail.pr.id} pr={prDetail.pr} report={prDetail.report} onBack={onBack} onTriggerReview={onTriggerReview} onInteractComment={onInteractComment} loading={loadingPr} />;
        }
        return loadingPr ? null : <div className="text-sm text-muted-foreground">PR not found.</div>;
      case 'agents':
        return <AgentRunners />;
      case 'issues':
        return <Issues />;
      case 'reliability':
        return <Reliability />;
      case 'rules':
        return <Rules />;
      case 'edge-queue':
        return <EdgeQueue currentUser={user} />;
      case 'schedules':
        return <ScheduleView />;
      case 'sbom':
        return <SbomManager />;
      case 'capabilities':
        return <Capabilities />;
      case 'settings':
        return config
          ? <Settings config={config} onSaveConfig={onSaveConfig} onSimulateWebhook={onSimulateWebhook} loading={false} currentUser={user} />
          : null;
      case 'workflows':
        return <WorkflowList onOpen={onOpenWorkflow} />;
      case 'workflow-edit':
        return <WorkflowEditor workflowId={selectedWorkflowId} onBack={onBackToWorkflows} />;
      default:
        return null;
    }
  };

  return (
    <>
      <AppShell
        user={user}
        groups={NAV_GROUPS}
        view={view}
        onNavigate={(id) => setView(id as View)}
        pageTitle={PAGE_TITLE[view] ?? 'Dashboard'}
        onSignOut={onSignOut}
        onOpenAccount={onOpenAccount}
        orgs={orgs}
        currentOrg={currentOrg}
        onSelectOrg={onSelectOrg}
        fullBleed={view === 'workflow-edit'}
      >
        {renderView()}
      </AppShell>

      {/* PR detail slides in from the right, overlaying whichever view is active. */}
      <Drawer open={!!selectedPr} onClose={onClosePr} title="Pull Request" width={720}>
        <PrDetails
          prId={prDetail?.pr.id ?? selectedPr ?? ''}
          pr={prDetail?.pr ?? null}
          report={prDetail?.report ?? null}
          onBack={onClosePr}
          onTriggerReview={onTriggerReview}
          onInteractComment={onInteractComment}
          loading={loadingPr}
        />
      </Drawer>
    </>
  );
}